<template>
  <el-card shadow="never">
    <template slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
      <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Payments' }">{{String($root.state.facilityData.code).toUpperCase()}} Payments</el-breadcrumb-item>
      </el-breadcrumb>
    </template>
    <el-card shadow="never" v-loading="loading">
       <el-row :gutter="20">
         <el-col class="date-picker">
          <el-date-picker
           v-model="date"
           @focus="() => setFieldsOnFacilityChange = true"
           type="month"
           placeholder="Select a month"
          />
         </el-col>
         <el-col :span="3" :offset="3">
            <el-input-number v-model="amount" :step="10" :min="0" placeholder="Amount" @change="() => setFieldsOnFacilityChange = true"></el-input-number>
         </el-col>
         <el-col :span="3" :offset="3">
            <el-input-number v-model="amountPaid" :step="10" :min="0" placeholder="Amount Paid" label="Total Amount" @change="() => setFieldsOnFacilityChange = true"></el-input-number>
         </el-col>
         <el-col :span="6" :offset="3">
           <el-button type="primary" round @click="getTransactions">Search</el-button>
           <el-button type="primary" round @click="exportTransactions" :disabled="transactionsEmpty">Export</el-button>
         </el-col>
       </el-row>
     </el-card>
      <el-table v-loading="loading" :data="transactions" @row-click="routeToCustomer">
        <el-table-column label="Member" :width="200">
          <template slot-scope="scope">
            {{ scope.row && scope.row.customer && scope.row.customer.company ? scope.row.customer.company : '--' }}
            <!-- <el-link :href="`/payments/${this.facility}/${scope.row.id}`" type="primary">{{ scope.row.id }}</el-link> -->
          </template>
        </el-table-column>
        <el-table-column label="Created At" :width="200" >
          <template slot-scope="scope">
            {{ formatDate(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="Initiated By" prop="initiatedBy" :width="250"></el-table-column>
        <el-table-column label="type" prop="type" :width="150">
          <template slot="header">
            <el-select
              label="Type"
              v-model="typeSearch"
              size="small"
              placeholder="Type"
              no-match-text="Type not set"
              no-data-text="Type not set"
              @change="filterStatus"
            >
              <el-option
                v-for="paymentType in paymentTypes"
                :key="paymentType"
                :label="paymentType"
                :value="paymentType"
              >
              </el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          property="status"
          align="right"
          :width="120"
        >
          <template slot="header">
            <el-select
              class="status"
              label="Lease Status"
              v-model="search"
              size="small"
              placeholder="Status"
              no-match-text="Status not set"
              no-data-text="Status not set"
              @change="filterStatus"
            >
              <el-option
                v-for="status in paymentStatuses"
                :key="status"
                :label="status.label"
                :value="status.value"
              >
              </el-option>
            </el-select>
          </template>
          <template slot-scope="scope">
             <el-tag v-if="!scope.row.success" :type="scope.row.status.toUpperCase() === 'FAILED' ? 'danger' : 'warning'">{{ scope.row.status.toUpperCase() }}</el-tag>
             <el-tag v-else :type="success">{{ scope.row.status.toUpperCase() }}</el-tag>
           </template>
        </el-table-column>
        <el-table-column label="Wallet Amount" prop="walletAmt" :width="150" align="center">
           <template slot-scope="scope">
             <el-tag effect="plain">
               {{ scope.row.walletAmt | currencySign($root.state.facilityData) }}
             </el-tag>
           </template>
        </el-table-column>
        <el-table-column label="Total Amount" prop="totalAmt" :width="150" align="center">
          <template
            v-if="scope.row.type === 'CREDIT_CARD'" slot-scope="scope">
              <el-tag effect="plan">{{ scope.row.totalAmt | currencySign($root.state.facilityData) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Transaction ID" :width="150">
          <template slot-scope="scope">
            <el-button type="text" @click="() => routeToTransaction(scope.row)">
              {{ scope.row.id }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="Last 4 digits" :width="150">
          <template slot-scope="scope">
            <div>{{ scope.row.last4 }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Description" prop="description" :width="350"></el-table-column>
      </el-table>
      <el-pagination
        hide-on-single-page
        v-show="!transactionsLoading"
        background
        :total="totalCount"
        layout="prev, pager, next"
        @current-change="handlePages"
        :page-size="50"
      >
      </el-pagination>
  </el-card>
</template>


<script>
import axios from 'axios';
import { FACILITY_TRANSACTIONS_API_BASE } from '../env';
import { paymentStatuses } from '../utils/status';
import exportToCsv, { saveCsv } from '../utils/exportToCsv';

export default {
  name: 'FacilityPayments',
  props: {
    facility: { type: String, required: true },
  },
  mounted() {
    this.abortController = new AbortController();
  },
  watch: {
    date() {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
    },
    async facility() {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
      this.loading = true;
      this.setFieldsOnFacilityChange = true;
      await this.getTransactions();
      this.loading = false;
    },
  },
  computed: {
    transactionsEmpty() {
      return Array.isArray(this.transactions) && !this.transactions.length;
    },
  },
  data() {
    return {
      transactions: [],
      search: '',
      abortController: null,
      paymentStatuses,
      typeSearch: '',
      paymentTypes: ['CREDIT_CARD', 'ACH', 'CHEQUE', 'WIRE', 'ALL'],
      totalCredit: 0,
      transactionsLoading: false,
      loading: false,
      date: null,
      amount: null,
      amountPaid: null,
      pages: 0,
      totalCount: 0,
      currentPage: 0,
      setFieldsOnFacilityChange: false,
      offset: this.$root.state.facilityData.offset,
    };
  },
  methods: {
    formatDate(dateString) {
      return new Date(dateString.slice(0, dateString.length - 1)).toLocaleString();
    },
    prepareTransactionsData(transactions) {
      return transactions.map((transaction) => {
        const id = transaction.id;
        // remove the comma from locale string so it doesnt interfere with the separator
        const createdAt = transaction.createdAt;
        const initiatedBy = transaction.createdBy;
        const type = transaction.type;
        const status = transaction.events[transaction.events.length - 1].status;
        const walletAmt = transaction.amount;
        const success = transaction.success;
        const description = transaction.details ? transaction.details.replace(',', ' ') : '--';
        const customerId = transaction.customer && transaction.customer._id ? transaction.customer._id : '';
        const customer = transaction.customer;

        let totalAmt = transaction.amount;
        if (transaction.creditCardFee && transaction.enabledCreditCardFee) {
          totalAmt = success ? transaction.amountPaid + transaction.creditCardFee : 0;
        }

        let last4;
        if (transaction.source) {
          last4 = transaction.source;
        }
        if (transaction.type === 'ACH' && transaction.events[1] && transaction.events[1].details && transaction.events[1].details.transaction && transaction.events[1].details.transaction.usBankAccount && transaction.events[1].details.transaction.usBankAccount.last4) {
          last4 = transaction.events[1].details.transaction.usBankAccount.last4;
        } else if (transaction.type === 'ACH' && transaction.events[1] && transaction.events[1].details && transaction.events[1].details.result && transaction.events[1].details.result.transaction && transaction.events[1].details.result.transaction.usBankAccount && transaction.events[1].details.result.transaction.usBankAccount.last4) {
          last4 = transaction.events[1].details.result.transaction.usBankAccount.last4;
        } else if (transaction.type === 'CREDIT_CARD' && transaction.events[1] && transaction.events[1].details && transaction.events[1].details.transaction && transaction.events[1].details.transaction.creditCard && transaction.events[1].details.transaction.creditCard.last4) {
          last4 = transaction.events[1].details.transaction.creditCard.last4;
        } else if (transaction.type === 'CREDIT_CARD' && transaction.events[1] && transaction.events[1].details && transaction.events[1].details && transaction.events[1].details.result && transaction.events[1].details.result.transaction && transaction.events[1].details.result.transaction.creditCard && transaction.events[1].details.result.transaction.creditCard.last4) {
          last4 = transaction.events[1].details.result.transaction.creditCard.last4;
        } else {
          last4 = '--';
        }


        return {
          id,
          createdAt,
          initiatedBy,
          type,
          status,
          success,
          walletAmt,
          totalAmt,
          last4: !last4 || last4 === '--' ? last4 : `********${last4}`,
          description,
          customerId,
          customer,
        };
      });
    },
    getFieldsToExport(object) {
      const {
        id,
        createdAt,
        initiatedBy,
        type,
        status,
        walletAmt,
        totalAmt,
        last4,
        description,
      } = object;

      return {
        id,
        createdAt: new Date(createdAt).toLocaleString().replace(',', ''),
        initiatedBy,
        type,
        status: status.toUpperCase(),
        walletAmt,
        totalAmt,
        last4,
        description,
      };
    },
    async exportTransactions() {
      const allTransactions = await this.getAllTransactionsForExport();
      const transactions = allTransactions.map(this.getFieldsToExport);
      const facilityPaymentsCsv = exportToCsv({
        Id: 'Transaction ID',
        CreatedAt: 'Created At',
        InitiatedBy: 'Initiated By',
        Type: 'Type',
        Status: 'Status',
        WalletAmt: 'Wallet Amount',
        TotalAmt: 'Total Amount',
        last4: 'Last 4 Digits',
        Description: 'Description',
      }, transactions);

      saveCsv(facilityPaymentsCsv, `Facility Payment Report ${this.date}`);
    },
    async handlePages(newPage) {
      this.currentPage = newPage;
      this.loading = true;
      await this.getTransactions();
      this.loading = false;
    },
    async filterStatus() {
      await this.getTransactions();
    },
    async getTransactions() {
      this.loading = true;

      this.transactions = [];
      if (this.setFieldsOnFacilityChange) {
        this.totalCount = 0;
        this.pages = 0;
        this.currentPage = 0;
        this.setFieldsOnFacilityChange = false;
      }

      try {
        const response = await axios.get(`${FACILITY_TRANSACTIONS_API_BASE}/v1/${this.facility}`, {
          signal: this.abortController.signal,
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
          params: {
            ...(this.date ? { month: this.date.getMonth(), year: this.date.getFullYear() } : {}),
            ...(this.amount ? { amount: this.amount } : {}),
            ...(this.amountPaid ? { amountPaid: this.amountPaid } : {}),
            ...(this.currentPage ? { pageNo: this.currentPage } : {}),
            ...(!this.typeSearch || this.typeSearch === 'ALL' ? { excludeTypes: 'CREDIT_WITHOUT_PAYMENT' } : { type: this.typeSearch }),
            ...(!this.search || this.search === 'All' ? {} : { statusQuery: this.search }),
            offset: this.offset,
          },
        });
        const transactionsPagination = response.data.result.pagination;

        this.totalCount = transactionsPagination.totalCount;
        this.currentPage = transactionsPagination.pageNo;

        this.transactions = this.prepareTransactionsData(response.data.result.transactions);
      } catch (err) {
        this.$message.error({
          message: err.response && err.response.data ? err.response.data : err.message,
        });
      }
      this.transactionsLoading = false;
      this.loading = false;
    },
    async getAllTransactionsForExport() {
      this.loading = true;

      try {
        const response = await axios.get(`${FACILITY_TRANSACTIONS_API_BASE}/v1/${this.facility}`, {
          signal: this.abortController.signal,
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
          params: {
            ...(this.date ? { month: this.date.getMonth(), year: this.date.getFullYear() } : {}),
            ...(this.amount ? { amount: this.amount } : {}),
            ...(this.amountPaid ? { amountPaid: this.amountPaid } : {}),
            ...(!this.typeSearch || this.typeSearch === 'ALL' ? { excludeTypes: 'CREDIT_WITHOUT_PAYMENT' } : { type: this.typeSearch }),
            ...(!this.search || this.search === 'All' ? {} : { statusQuery: this.search }),
            limit: this.totalCount + 1,
            offset: this.offset,
          },
        });

        this.loading = false;
        this.transactionsLoading = false;
        return this.prepareTransactionsData(response.data.result.transactions);
      } catch (err) {
        this.$message.error({
          message: err.response && err.response.data ? err.response.data : err.message,
        });
        this.transactionsLoading = false;
        this.loading = false;
        return null;
      }
    },
    routeToCustomer(row) {
      if (this.facility) {
        this.$router.push({ name: 'Facility Customer', params: { id: row.customerId, facility: this.facility, enabledTab: 'payments' } });
      }
    },
    routeToTransaction(row) {
      this.$router.push({
        name: 'Facility Payments Transaction',
        params: {
          id: row.id,
          facility: this.facility,
        },
      });
    },
  },
};
</script>

  <style scoped>

  .date-picker{
    padding-bottom: 15px;
  }

  </style>



// WEBPACK FOOTER //
// src/components/FacilityPayments.vue